"use client";

import { Minus, Plus } from "lucide-react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { Accordion, AccordionContent, AccordionItem } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  id: "f1",
  question: "Peut-on commencer l’accompagnement après la naissance? ",
  answer: "Oui. Je peux intervenir dès les premiers jours de ton retour à la maison, même si nous ne nous sommes pas rencontrées pendant la grossesse.\n\nLorsque c’est possible, je recommande toutefois une rencontre prénatale. Elle nous permet de faire connaissance, de parler de tes besoins et de préparer un accompagnement qui te ressemble avant l’arrivée de bébé."
}, {
  id: "f2",
  question: "Où te déplaces-tu? ",
  answer: "Je propose des accompagnements à domicile entre Genève et Morges, notamment dans la région de Nyon et sur La Côte.\n\nLa faisabilité du déplacement est confirmée lors de notre premier échange, selon ton lieu de résidence et mes disponibilités."
}, {
  id: "f3",
  question: "Peux-tu faire des courses ou passer à la pharmacie?",
  answer: "Oui. Je peux récupérer quelques courses, des produits de pharmacie ou ce dont tu as besoin pour la maison et le bébé.\n\nLe temps consacré aux courses et aux déplacements est compris dans la durée prévue de la visite."
}, {
  id: "f4",
  question: "Est-ce que je peux me reposer pendant que tu es là?",
  answer: "Oui, et c’est même vivement encouragé.\n\nTu peux dormir, prendre une douche, manger tranquillement, fermer les yeux ou simplement profiter d’un moment pour toi. Je peux m’occuper de certaines tâches du quotidien et veiller sur ton bébé à proximité, selon ce qui te met à l’aise.\n\nTu peux aussi choisir de discuter si tu en ressens le besoin."
}, {
  id: "f5",
  question: "Proposez-vous un soutien après les premières semaines ?",
  answer: "Oui. Le postpartum ne s’arrête pas après les premières semaines: il peut s’étendre sur plusieurs mois, voire plusieurs années. Je peux venir te soutenir quand tu en ressens le besoin, même si ton bébé est déjà plus grand. L’accompagnement s’adapte à ta réalité du moment, à ta fatigue, à ton quotidien et aux besoins de ta famille."
}, {
  id: "f6",
  question: "Peux-tu t’occuper des ainées?",
  answer: "Je peux leur offrir un temps d’attention ponctuel : jouer, dessiner, lire une histoire ou les accompagner dans une activité calme.\n\nMon accompagnement reste toutefois principalement centré sur la mère ou le parent qui a accouché, ainsi que sur le nouveau-né. Je ne remplace donc pas une garde d’enfants régulière."
}, {
  id: "f7",
  question: "Et pour le linge et la maison?",
  answer: "Je peux t’aider avec différentes tâches légères du quotidien, par exemple: faire la vaisselle; nettoyer les biberons et le matériel de bébé; lancer, étendre, plier ou ranger une lessive; changer les draps ou les serviettes; vider les poubelles; effectuer un peu de rangement ou de ménage léger. L’objectif est de diminuer ta charge mentale, pas de réaliser un nettoyage complet du logement."
}, {
  id: "f8",
  question: "Puis-je offrir un accompagnement en cadeau?",
  answer: "Absolument. C'est un cadeau de naissance précieux et utile qui soutient réellement les nouveaux parents. Des bons cadeaux personnalisés sont disponibles sur demande."
}, {
  id: "f9",
  question: "Le Batch Cooking est-il adapté aux régimes spécifiques?",
  answer: "Oui, je m'adapte totalement à vos préférences alimentaires, allergies ou régimes spécifiques (végétarien, sans gluten, etc.) pour vous proposer des menus qui vous conviennent."
}, {
  id: "f10",
  question: "Combien de temps dure une visite à domicile?",
  answer: "En général, une visite dure 4 heures, ce qui nous laisse le temps d'échanger, de préparer des repas ou de s'occuper du bien-être de la maman en toute sérénité."
}, {
  id: "f11",
  question: "Est-ce que je peux essayer une première séance avant de pursuivre avec un forfait?",
  answer: "Pour un accompagnement à domicile, il est tout à fait possible de commencer par une première visite, puis de décider ensuite si tu souhaites poursuivre. Tarif pour une séance essai: CHF 320."
}];
export default function Questions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1962" id="questions-fréquentes" className="bg-background text-foreground py-20 sm:py-28 lg:py-32">
      <div ref={ref} className={`mx-auto w-full max-w-[720px] px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="text-center">
          <p className="font-default text-sm font-semibold uppercase tracking-[0.2em] text-[--caramel]">
            Questions fréquentes
          </p>
          <h2 className="mt-4 font-default text-3xl font-normal leading-tight text-foreground sm:text-4xl">Réponses à vos questions</h2>
        </div>

        <Accordion type="single" collapsible className="mt-12 w-full">
          {faqs.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border-b border-[--beige]">
              <AccordionPrimitive.Header className="flex">
                <AccordionPrimitive.Trigger className="group flex flex-1 items-start justify-between gap-6 py-6 text-left outline-none transition-colors focus-visible:text-[--caramel]">
                  <span className="font-default text-lg font-normal leading-snug text-foreground sm:text-xl">
                    {faq.question}
                  </span>
                  <span className="relative mt-1 flex size-6 shrink-0 items-center justify-center text-[--caramel]">
                    <Plus className="size-5 transition-all duration-300 ease-out group-data-[state=open]:rotate-90 group-data-[state=open]:opacity-0" />
                    <Minus className="absolute size-5 opacity-0 transition-all duration-300 ease-out group-data-[state=open]:opacity-100" />
                  </span>
                </AccordionPrimitive.Trigger>
              </AccordionPrimitive.Header>
              <AccordionContent className="pb-6 pr-12">
                <p className="font-default text-base leading-relaxed text-[--brun]">
                  {faq.answer}
                </p>
              </AccordionContent>
            </AccordionItem>)}
        </Accordion>
      </div>
    </section>;
}